// HomeCard.tsx
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { styled } from "@mui/material/styles";
import { Box, Typography, Grid, Paper, Button } from "@mui/material";
import { useStyles } from "./styles";
import { useGetModelsService } from "@hooks";

const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: "#1a1a20",
  ...theme.typography.body2,
  padding: "2rem",
  textAlign: "center",
  color: "#cfcfd6",
}));

const PAGE_SIZE = 24;

const HomeCard: React.FC = () => {
  const navigate = useNavigate();
  const classes = useStyles();
  const {
    handleGetModels,
    homeModels,
    showModelsDisclaimer,
  } = useGetModelsService();

  useEffect(() => {
    if (!homeModels.length) handleGetModels();
  }, [handleGetModels, homeModels.length]);

  const [visible, setVisible] = useState<number>(PAGE_SIZE);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const total = homeModels.length;

  useEffect(() => {
    setVisible(PAGE_SIZE);
  }, [homeModels]);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || visible >= total || typeof IntersectionObserver === "undefined") return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setVisible((v) => v + PAGE_SIZE);
        }
      },
      { rootMargin: "400px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [visible, total]);

  const handleClick = (id: string) => {
    navigate(`/show/${id}`);
  };

  const getMembershipTagClass = (membership: string) => {
    let tagClass = `${classes.cardImage} ${classes.tag} ${classes.tagMembership}`;
    if (membership === "VIP") return tagClass + ` ${classes.tagVip}`;
    if (membership === "TOP") return tagClass + ` ${classes.tagTop}`;
    return tagClass + ` ${classes.tagFree}`;
  };

  const getProfileStatusTagClass = (verified: boolean) => {
    const tagClass = `${classes.tagProfileStatus}`;
    return verified
      ? tagClass + ` ${classes.tagVerified}`
      : tagClass + ` ${classes.tagNotVerified}`;
  };

  return (
    <Grid container spacing={2}>
      {showModelsDisclaimer && (
        <Grid item xs={12}>
          <Item>
            <Typography variant="h2" sx={{ color: "#fff", fontSize: "2rem" }}>
              Ops.
            </Typography>
            <Typography variant="body1" sx={{ color: "#cfcfd6" }}>
              There are no models available in your area. Please, take a look at
              these ones.
            </Typography>
          </Item>
        </Grid>
      )}
      {homeModels.slice(0, visible).map((model) => {
        return (
          <Grid key={`model-${model.id}`} item xs={6} sm={4} md={2.4}>
            <Box
              className={classes.cardContainer}
              onClick={() => handleClick(model.topic)}
            >
              <div
                className={getMembershipTagClass(model.membership)}
                data-membership={model.membership}
              >
                <div
                  className={getProfileStatusTagClass(model.verified)}
                  data-verification={model.verified ? "VERIFIED" : "NOT VERIFIED"}
                />
                <img
                  src={model.imageUrl}
                  alt={model.fullname}
                  loading="lazy"
                  className={classes.cardImage}
                />
                <Box className={classes.cardOverlay}>
                  <Box className={classes.cardName}>{model.fullname}</Box>
                  <Box className={classes.cardCity}>
                    {model.contact.city
                      ? model.contact.city.name
                      : model.contact.country?.name}
                  </Box>
                </Box>
              </div>
            </Box>
          </Grid>
        );
      })}
      {visible < total && (
        <Grid item xs={12}>
          <div ref={sentinelRef} style={{ textAlign: "center", padding: "1rem" }}>
            <Button
              variant="outlined"
              onClick={() => setVisible((v) => v + PAGE_SIZE)}
              sx={{ color: "#cfcfd6", borderColor: "#444" }}
            >
              Load more
            </Button>
          </div>
        </Grid>
      )}
    </Grid>
  );
};

export { HomeCard };
